'use client';

import { useState, useMemo } from 'react';
import { Check, ChevronsUpDown, Plus, X, Loader2 } from 'lucide-react';
import { toast } from '@/lib/toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command';
import { useDebouncedValue } from '@/lib/hooks/use-debounced-value';
import { useSearchContacts } from '@/lib/services/safe-custody';
import type { SafeCustodyContact } from '@/lib/services/safe-custody/types';
import {
  createGlobalContactAction,
  createContactAction
} from '@/lib/services/contacts/client';
import { getContactDisplayName } from '@/lib/services/contacts/utils';

/**
 * Selected contact type.
 *
 * Carries the full name contract `getContactDisplayName` reads — organisation
 * name and the deceased flags included — so a selected row renders exactly as
 * its search result did. Narrowing this is what let an organisation contact
 * survive selection as `Contact #<id>` (VALE-651).
 */
export interface SelectedContact {
  id: number;
  firstName?: string;
  middleName?: string | null;
  lastName?: string;
  email?: string | null;
  organisationName?: string | null;
  dateOfDeath?: string | null;
  isDeceased?: boolean | null;
}

interface ContactSearchFieldProps {
  /** Currently selected contacts */
  selectedContacts: SelectedContact[];

  /** Callback when contacts change */
  onChange: (contacts: SelectedContact[]) => void;

  /** Label for the field */
  label?: string;

  /** Placeholder text */
  placeholder?: string;

  /** Whether field is disabled */
  disabled?: boolean;

  /** Maximum number of contacts allowed (undefined = unlimited) */
  maxContacts?: number;

  /** Whether to allow creating new contacts inline */
  allowCreate?: boolean;

  /** Matter ID - if provided, contacts are created for this matter; otherwise global contacts */
  matterId?: number;
}

/**
 * ContactSearchField Component
 *
 * A searchable contact selector with inline creation capability.
 * Follows the same pattern as InstitutionSearchField.
 *
 * Features:
 * - Backend fuzzy search via useSearchContacts
 * - Multiple contact selection
 * - Inline "Add new contact" form
 * - Selected contacts displayed as removable full-width rows
 *
 * Every name — in the results list and after selection — comes from
 * `getContactDisplayName`, the one contact-name formatter (VALE-651).
 */
export function ContactSearchField({
  selectedContacts,
  onChange,
  label = 'Clients',
  placeholder = 'Search contacts...',
  disabled = false,
  maxContacts,
  allowCreate = true,
  matterId
}: ContactSearchFieldProps) {
  const [open, setOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const [showAddNew, setShowAddNew] = useState(false);
  const [newContactFirstName, setNewContactFirstName] = useState('');
  const [newContactLastName, setNewContactLastName] = useState('');
  const [newContactEmail, setNewContactEmail] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  const debouncedSearch = useDebouncedValue(searchValue, 300);

  // Search contacts via backend
  const { data: searchResults, isLoading: isSearching } = useSearchContacts(
    debouncedSearch,
    {
      enabled: debouncedSearch.length >= 2
    }
  );

  // Filter out already selected contacts from search results
  const filteredSearchResults = useMemo(() => {
    if (!searchResults?.items?.length) return [];
    const selectedIds = new Set(selectedContacts.map((c) => c.id));
    return searchResults.items.filter((c) => !selectedIds.has(c.id));
  }, [searchResults, selectedContacts]);

  // Check if max contacts reached
  const maxReached = maxContacts
    ? selectedContacts.length >= maxContacts
    : false;

  const handleSelectContact = (contact: SafeCustodyContact) => {
    onChange([
      ...selectedContacts,
      {
        id: contact.id,
        firstName: contact.firstName,
        middleName: contact.middleName,
        lastName: contact.lastName,
        email: contact.email,
        organisationName: contact.organisationName,
        dateOfDeath: contact.dateOfDeath,
        isDeceased: contact.isDeceased
      }
    ]);
    setSearchValue('');
    setOpen(false);
  };

  const handleRemoveContact = (contactId: number) => {
    onChange(selectedContacts.filter((c) => c.id !== contactId));
  };

  const handleAddNew = async () => {
    if (!newContactFirstName.trim() && !newContactLastName.trim()) return;

    setIsCreating(true);

    try {
      // Build contact data - matter-specific endpoint requires role flags
      const contactData = {
        firstName: newContactFirstName.trim() || '',
        lastName: newContactLastName.trim() || '',
        email: newContactEmail.trim() || undefined,
        // Required fields for matter-specific contact creation
        isLegalPersonalRepresentative: false,
        isBeneficiary: false
      };

      // Create contact via API - use matter-specific or global endpoint
      const result = matterId
        ? await createContactAction(matterId, contactData)
        : await createGlobalContactAction(contactData);

      if (!result.success) {
        toast.error(result.error || 'Failed to create contact');
        return;
      }

      // Add the newly created contact (with real ID) to selection
      const newContact: SelectedContact = {
        id: result.data!.id,
        firstName: result.data!.firstName,
        lastName: result.data!.lastName,
        email: result.data!.email || null,
        organisationName: result.data!.organisationName || null
      };

      onChange([...selectedContacts, newContact]);
      toast.success('Contact created successfully');

      // Reset form
      setShowAddNew(false);
      setNewContactFirstName('');
      setNewContactLastName('');
      setNewContactEmail('');
    } catch (error) {
      console.error('Error creating contact:', error);
      toast.error('Failed to create contact');
    } finally {
      setIsCreating(false);
    }
  };

  const handleCancelAddNew = () => {
    setShowAddNew(false);
    setNewContactFirstName('');
    setNewContactLastName('');
    setNewContactEmail('');
  };

  return (
    <div className="space-y-2">
      {label && <Label>{label}</Label>}

      {/* Search dropdown - only show if not at max */}
      {!maxReached && !showAddNew && (
        <>
          <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
              <Button
                role="combobox"
                aria-expanded={open}
                disabled={disabled}
                variant="faux-input"
                size="xs"
                width="full"
                align="between"
                pad="lg"
                weight="normal"
              >
                <span className="text-muted-foreground">{placeholder}</span>
                <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-full min-w-[300px] p-0" align="start">
              <Command shouldFilter={false}>
                <CommandInput
                  placeholder={placeholder}
                  value={searchValue}
                  onValueChange={setSearchValue}
                />
                <CommandList>
                  {isSearching ? (
                    <div className="flex items-center justify-center p-4">
                      <Loader2 className="text-muted-foreground h-4 w-4 animate-spin" />
                    </div>
                  ) : debouncedSearch.length < 2 ? (
                    <div className="text-muted-foreground p-4 text-center text-sm">
                      Type at least 2 characters to search
                    </div>
                  ) : filteredSearchResults.length === 0 ? (
                    <CommandEmpty>No contacts found.</CommandEmpty>
                  ) : (
                    <CommandGroup>
                      {filteredSearchResults.map((contact) => (
                        <CommandItem
                          key={contact.id}
                          value={String(contact.id)}
                          onSelect={() => handleSelectContact(contact)}
                        >
                          <Check
                            className={cn(
                              'mr-2 h-4 w-4',
                              selectedContacts.some((c) => c.id === contact.id)
                                ? 'opacity-100'
                                : 'opacity-0'
                            )}
                          />
                          <div className="flex flex-col">
                            <span>{getContactDisplayName(contact)}</span>
                            {contact.email && (
                              <span className="text-muted-foreground text-xs">
                                {contact.email}
                              </span>
                            )}
                          </div>
                        </CommandItem>
                      ))}
                    </CommandGroup>
                  )}
                </CommandList>
              </Command>
            </PopoverContent>
          </Popover>

          {/* Create contact link */}
          {allowCreate && !disabled && (
            <div className="text-muted-foreground flex items-center gap-2 text-sm">
              <span>Can't find the contact?</span>
              <Button
                size="xs"
                density="compact"
                gap="standard"
                tone="accent"
                weight="normal"
                type="button"
                variant="ghost"
                onClick={() => setShowAddNew(true)}
              >
                <Plus className="h-3 w-3" />
                Create contact
              </Button>
            </div>
          )}
        </>
      )}

      {/* Create contact form */}
      {showAddNew && (
        <div className="bg-muted/50 space-y-4 rounded-lg border p-4">
          <div className="space-y-1">
            <h4 className="text-sm font-medium">Create New Contact</h4>
            <p className="text-muted-foreground text-xs">
              Enter contact details to create a new contact
            </p>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="newContactFirstName">First Name *</Label>
              <Input
                id="newContactFirstName"
                value={newContactFirstName}
                onChange={(e) => setNewContactFirstName(e.target.value)}
                placeholder="John"
                className="h-8"
                disabled={isCreating}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="newContactLastName">Last Name *</Label>
              <Input
                id="newContactLastName"
                value={newContactLastName}
                onChange={(e) => setNewContactLastName(e.target.value)}
                placeholder="Smith"
                className="h-8"
                disabled={isCreating}
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="newContactEmail">Email</Label>
            <Input
              id="newContactEmail"
              type="email"
              value={newContactEmail}
              onChange={(e) => setNewContactEmail(e.target.value)}
              placeholder="john.smith@example.com"
              className="h-8"
              disabled={isCreating}
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="xs"
              density="compact"
              onClick={handleCancelAddNew}
              disabled={isCreating}
            >
              Cancel
            </Button>
            <Button
              type="button"
              size="xs"
              density="compact"
              gap="standard"
              onClick={handleAddNew}
              disabled={
                isCreating ||
                (!newContactFirstName.trim() && !newContactLastName.trim())
              }
            >
              {isCreating && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
              Create Contact
            </Button>
          </div>
        </div>
      )}

      {/* Selected Contacts List */}
      {selectedContacts.length > 0 && (
        <div className="space-y-2">
          {selectedContacts.map((contact) => (
            <div
              key={contact.id}
              className="flex items-center justify-between rounded-md bg-[var(--input-bg)] px-3 py-2"
            >
              <div className="flex flex-col">
                <span className="text-sm">
                  {getContactDisplayName(contact)}
                </span>
                {contact.email && (
                  <span className="text-muted-foreground text-xs">
                    {contact.email}
                  </span>
                )}
              </div>
              <Button
                tone="muted"
                type="button"
                variant="ghost"
                size="icon-2xs"
                onClick={() => handleRemoveContact(contact.id)}
                disabled={disabled}
              >
                <X className="h-4 w-4" />
              </Button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
